import type { ReactNode } from 'react'
import { useState } from 'react'
import { Button, Modal, toastManager } from '@systemeio/components'
import { useTranslation } from 'next-i18next'
import type { KeyedMutator } from 'swr'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { cancelSubscription } from '../api/cancel-subscriptions-api'
import type {
  ManageSubscriptionsApiInterface,
  ManageSubscriptionsInterface,
} from '../types/manage-subscriptions-interface'

interface CancelSubscriptionModalProps {
  opened: boolean
  onClose: () => void
  subscription: ManageSubscriptionsInterface
  mutate: KeyedMutator<ManageSubscriptionsApiInterface>
}

function CancelSubscriptionModal({
  opened,
  onClose,
  subscription,
  mutate,
}: CancelSubscriptionModalProps): ReactNode {
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const [isFetching, setIsFetching] = useState(false)

  const handleConfirm = async (): Promise<void> => {
    try {
      setIsFetching(true)
      await cancelSubscription(
        subscription.id,
        subscription.subscriptionItems[0].id,
        subscription.paymentProcessor,
      )
      await mutate(data => {
        if (data) {
          return {
            items: data.items.map(item => {
              if (item.id === subscription.id) item = { ...item, status: 'cancelled' }
              return item
            }),
            hasMore: data.hasMore,
          }
        }
      }, false)
      setIsFetching(false)
      onClose()
      toastManager.add({ type: 'success', title: t('profile.subscriptions.unsubscribed') })
    } catch (error_) {
      setIsFetching(false)
      if (error_ instanceof BadRequest) {
        if (error_.errors.common) {
          setError(error_.errors.common.join(''))
        }
      } else if (error_ instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
      }
    }
  }

  return (
    <Modal.Root
      open={opened}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <Modal.Popup>
        <Modal.Header>
          <Modal.Title>{t('confirmation_modal.default_title')}</Modal.Title>
        </Modal.Header>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">
          <p>{t('profile.subscriptions.unsubscribe_confirmation')}</p>
          {error && <p className="text-center text-sm text-text-error">{error}</p>}
        </div>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isFetching}>
            {t('confirmation_modal.cancel')}
          </Button>
          <Button variant="primary" isLoading={isFetching} onClick={handleConfirm}>
            {t('confirmation_modal.confirm')}
          </Button>
        </Modal.Footer>
      </Modal.Popup>
    </Modal.Root>
  )
}

export default CancelSubscriptionModal
